import { useContext, useRef, useState } from "react";
import PropTypes from "prop-types";
import { CreativeReviewContext } from "../../../contexts/contexts";
import Grid from "@mui/material/Unstable_Grid2";
import Carousel from "react-material-ui-carousel";
import {
  Box,
  TextField,
  Button,
  Paper,
  Checkbox,
  FormControlLabel,
  Link,
  Tabs,
  Tab,
} from "@mui/material";
import {
  ArrowCircleLeftOutlined,
  ArrowCircleRightOutlined,
  AttachFileOutlined,
  BlockOutlined,
  SendOutlined,
  ZoomIn,
} from "@mui/icons-material";

import { AppAlert } from "../appAlert/AppAlert";
import { TabPanel } from "../tabPanel/TabPanel";
import { ProductListing as PathmaticsProductListing } from "../../pathmatics/productListing/ProductListing";
import { ProductListing } from "../productListing/ProductListing";

import {
  ACTIVATION_FILTER_OPTIONS,
  MAPPING_OPTIONS,
  MAPPING_KEYS_STATUSES,
  CREATIVES_MEDIA_DOMAIN,
  DATA_SOURCE,
} from "../../../constants";

import { initLocalization } from "../../../utils/helpers";
import { updateCreativeStrings } from "./locale";
import { productListingStrings } from "../../pathmatics/productListing/locale";
import mediaPlaceholderImage from "../../../assets/media-placeholder.jpg";
import "./style.scss";

const BUFFER_SIZE = 7;

const buildCreatives = (data) => {
  const dataCopy = JSON.parse(JSON.stringify(data));
  dataCopy.forEach((item, idx) => (item.index = idx));
  return dataCopy;
};

const buildCreative = (index, slideIndex, data) => {
  return {
    index,
    slideIndex,
    id: data[index]?.id,
    prodId: data[index]?.product?.id,
    productId: data[index]?.product?.sourceProductId,
    status: data[index]?.status,
    isValidVisual: data[index]?.isValidVisual,
  };
};

const createChunk = (currentIndex, dataSet, offset = 0) => {
  let chunk = [];
  const prevBufferLastIndex = currentIndex - BUFFER_SIZE;
  const nextBufferLastIndex = currentIndex + BUFFER_SIZE;

  // prev buffer
  if (prevBufferLastIndex < 0) {
    chunk = [
      ...chunk,
      ...dataSet.slice(prevBufferLastIndex),
      ...dataSet.slice(0, BUFFER_SIZE + prevBufferLastIndex),
    ];
  } else {
    chunk = [...chunk, ...dataSet.slice(prevBufferLastIndex, currentIndex)];
  }

  // next buffer
  if (nextBufferLastIndex >= dataSet.length) {
    chunk = [
      ...chunk,
      ...dataSet.slice(currentIndex, nextBufferLastIndex),
      ...dataSet.slice(0, nextBufferLastIndex + 1 - dataSet.length),
    ];
  } else {
    chunk = [...chunk, ...dataSet.slice(currentIndex, nextBufferLastIndex + 1)];
  }

  // chunk offsetting
  // Note: due to the fact that the carousel's index prop doesn't work correctly
  // when the same value is passed to it, the chunk array has to be offset by
  // one item alternatively. The original array index will be kept in chunkIndex.
  chunk = JSON.parse(JSON.stringify(chunk));

  chunk.forEach((item, idx) => (item.chunkIndex = idx));

  const offsetChunk = [...chunk.slice(offset), ...chunk.slice(0, offset)];

  return offsetChunk;
};

const createMedia = (type, details, videoRef) => {
  const item = {
    ...details,
    rawlocation: details.rawLocation,
    rawmediaTypeName: details.rawMediaTypeName,
  };

  return (
    <>
      {item?.[`${type}mediaTypeName`]?.length === 0 && (
        <img
          src={mediaPlaceholderImage}
          alt={item.name}
          className="creative-media-wrapper__media-placeholder"
        />
      )}
      {item?.[`${type}mediaTypeName`] === "image" && (
        <img
          src={item?.[`${type}location`]}
          alt={item.name}
          className="creative-media-wrapper__media-image"
        />
      )}
      {item?.[`${type}mediaTypeName`] === "video" && (
        <video
          className="creative-media-wrapper__media-video"
          controls
          muted
          onPlay={(e) => (videoRef.current = e.target)}
        >
          <source src={item?.[`${type}location`]} />
        </video>
      )}
      {item?.[`${type}mediaTypeName`]?.toLowerCase() === "html5" && (
        <iframe
          className="creative-media-wrapper__media-iframe"
          title={item?.id}
          src={item?.[`${type}location`]?.split(CREATIVES_MEDIA_DOMAIN)?.[1]}
        />
      )}
      {item?.[`${type}mediaTypeName`].length > 0 && (
        <Link
          className="zoom-creative"
          href={item[`${type}location`]}
          target="_blank"
        >
          <ZoomIn fontSize="inherit" />
        </Link>
      )}
    </>
  );
};

export const UpdateCreative = ({
  dataSource,
  creativeDetailsData,
  showProductListing = true,
  onClose,
}) => {
  initLocalization(updateCreativeStrings);

  const { metadataComponent, useValidVisual, useMappingKeyStatus } = useContext(
    CreativeReviewContext,
  );

  const chunkOffset = useRef(0);
  const videoRef = useRef(null);
  const [error, setError] = useState(null);
  const [creatives, setCreatives] = useState(
    buildCreatives(creativeDetailsData) || [],
  );
  const [selectedProduct, setSelectedProduct] = useState({});
  const [paperClipLabel, setPaperClipLabel] = useState("");
  const [changeAll, setChangeAll] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const useChunks = creatives.length > 2 * BUFFER_SIZE + 1;

  const [creative, setCreative] = useState(
    buildCreative(
      0,
      useChunks ? BUFFER_SIZE - chunkOffset.current : 0,
      creatives,
    ),
  );

  const [creativesChunk, setCreativesChunk] = useState(
    useChunks ? createChunk(0, creatives) : creatives,
  );

  const {
    isLoading: actionLoading,
    paperClipMappingKey,
    rejectMappingKey,
    validateMappingKey,
  } = useMappingKeyStatus(
    MAPPING_OPTIONS[`${dataSource?.toLowerCase()}Creative`],
  );

  const { isLoading: validVisualLoading, setValidVisual } = useValidVisual();

  const disablePaperClipButton = () => {
    return !creatives?.length || !paperClipLabel || actionLoading;
  };

  const disableRejectButton = () => {
    return !creatives?.length || actionLoading;
  };

  const disableValidateButton = () => {
    return !creatives?.length || !selectedProduct.id || actionLoading;
  };

  const updateCarousel = (remove = false) => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current = null;
    }

    let nextIndex = creative.index + 1;

    if (nextIndex >= creatives.length) {
      nextIndex = 0;
    }

    const newCreatives = buildCreatives(
      remove
        ? [...creatives].filter((item) => item.id !== creative.id)
        : [...creatives],
    );

    const nextCreativeIndex = newCreatives.findIndex(
      (item) => item.id === creatives[nextIndex]?.id,
    );

    let nextChunkIndex;

    if (useChunks) {
      const nextChunkCreative = creativesChunk.find(
        (item) => item.id === newCreatives[nextCreativeIndex].id,
      );

      let chunkIndex = nextChunkCreative.chunkIndex;

      if (
        chunkIndex + 1 >= creativesChunk.length ||
        chunkIndex - 1 < 0 ||
        remove
      ) {
        const offset = 1 - chunkOffset.current;

        chunkOffset.current = offset;
        chunkIndex = BUFFER_SIZE;

        setCreativesChunk(
          createChunk(
            remove
              ? newCreatives[nextCreativeIndex].index
              : nextChunkCreative.index,
            newCreatives,
            offset,
          ),
        );
      }

      nextChunkIndex = chunkIndex - chunkOffset.current;
    } else if (remove) {
      setCreativesChunk(newCreatives);
    }

    const newCreative = buildCreative(
      nextCreativeIndex,
      useChunks ? nextChunkIndex : nextCreativeIndex,
      newCreatives,
    );

    if (!newCreatives.length || changeAll) {
      onClose();
      return;
    }

    setCreatives(newCreatives);
    setCreative(newCreative);
  };

  const buildPayload = (creative, selectedProductId) => {
    const payload = {
      lineIds: !changeAll ? [creative.id] : creatives.map((c) => c.id),
    };

    if (selectedProductId) payload.productId = selectedProductId;

    return payload;
  };

  const handlePaperClipFieldChange = (e) => {
    setPaperClipLabel(e.target.value);
  };

  const handlePaperClipMappingKeys = () => {
    const data = {
      ...buildPayload(creative),
      paperclipLabel: paperClipLabel,
    };
    paperClipMappingKey(data)
      .then(() => updateCarousel(true))
      .catch((err) => setError(err));
  };

  const handleRejectMappingKeys = () => {
    const data = buildPayload(creative);
    rejectMappingKey(data)
      .then(() =>
        updateCarousel(creative.status !== MAPPING_KEYS_STATUSES.rejected),
      )
      .catch((err) => setError(err));
  };

  const handleValidateMappingKeys = () => {
    const data = buildPayload(creative, selectedProduct.id);
    validateMappingKey(data)
      .then(() => updateCarousel(creative.prodId !== selectedProduct.id))
      .catch((err) => setError(err));
  };

  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleSetValidVisual = async () => {
    const isValidVisual = !creative.isValidVisual;
    await setValidVisual({
      id: creative.id,
      isValidVisual,
    });
    setCreative((prev) => {
      const next = { ...prev };
      next.isValidVisual = isValidVisual;
      return next;
    });
    setCreatives((prev) => {
      const next = [...prev];
      next.find((item) => item.id === creative.id).isValidVisual =
        isValidVisual;
      return next;
    });
  };

  const handleCloseError = () => setError(false);

  return (
    <>
      <AppAlert
        key={error}
        open={Boolean(error)}
        type="error"
        onClose={handleCloseError}
      >
        {productListingStrings.errors[error]}
      </AppAlert>

      <Grid
        className="update-creative"
        container
        rowSpacing={{ xs: 3, md: 0 }}
        columnSpacing={{ xs: 0, md: 2 }}
        py={6}
        px={{ xs: 2, md: 6 }}
        disableEqualOverflow
      >
        <Grid
          md={
            showProductListing &&
            creative.status !== MAPPING_KEYS_STATUSES.waitingProductRequested
              ? 6
              : 12
          }
          xs={12}
        >
          <Paper
            sx={{ padding: "0.5rem", height: "100%", position: "relative" }}
          >
            <Carousel
              className="update-creative__carousel-wrapper"
              index={creative.slideIndex}
              animation="slide"
              autoPlay={false}
              swipe={false}
              navButtonsAlwaysVisible={true}
              fullHeightHover={false}
              onChange={(current, prev) => {
                if (videoRef.current) {
                  videoRef.current.pause();
                  videoRef.current = null;
                }

                let nextChunkIndex;

                if (useChunks) {
                  let chunkIndex = creativesChunk[current].chunkIndex;

                  if (
                    chunkIndex + 1 >= creativesChunk.length ||
                    chunkIndex - 1 < 0
                  ) {
                    const offset = 1 - chunkOffset.current;

                    chunkOffset.current = offset;
                    chunkIndex = BUFFER_SIZE;

                    setCreativesChunk(
                      createChunk(
                        creativesChunk[current].index,
                        creatives,
                        offset,
                      ),
                    );
                  }

                  nextChunkIndex = chunkIndex - chunkOffset.current;
                }

                setCreative(
                  buildCreative(
                    creativesChunk[current].index,
                    useChunks ? nextChunkIndex : current,
                    creatives,
                  ),
                );
              }}
              navButtonsWrapperProps={{
                className: "update-creative__nav-buttons-wrapper-props",
              }}
              navButtonsProps={{
                className: "update-creative__nav-buttons-props",
                disabled: creatives?.length <= 1 || actionLoading,
              }}
              PrevIcon={
                <Box className="update-creative__slider-buttons update-creative__slider-prev">
                  <ArrowCircleLeftOutlined
                    sx={{ width: "2.5rem", height: "2.5rem" }}
                  />
                  {updateCreativeStrings.prevButton}
                </Box>
              }
              NextIcon={
                <Box className="update-creative__slider-buttons update-creative__slider-next">
                  {updateCreativeStrings.nextButton}
                  <ArrowCircleRightOutlined
                    sx={{ width: "2.5rem", height: "2.5rem" }}
                  />
                </Box>
              }
              IndicatorIcon={creativesChunk.map(
                (item) => `${item.index + 1} / ${creatives.length}`,
              )}
              indicatorIconButtonProps={{
                className: "update-creative__indicator-icon-button-props",
                disabled: true,
              }}
              activeIndicatorIconButtonProps={{
                className:
                  "update-creative__active-indicator-icon-button-props",
              }}
              indicatorContainerProps={{
                className: "update-creative__indicator-container-props",
              }}
            >
              {creativesChunk?.map((item) => (
                <div
                  key={item.id}
                  style={{ display: "flex", height: "calc(100vh - 250px)" }}
                >
                  <div className="creative-media-wrapper">
                    {item?.rawMediaTypeName ? (
                      <>
                        <Box
                          sx={{
                            mb: 1,
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Tabs
                            value={selectedTab}
                            onChange={handleChangeTab}
                            aria-label="media tabs"
                          >
                            <Tab
                              label={updateCreativeStrings.preview}
                              id="tab-0"
                              aria-controls="tabpanel-0"
                            />
                            <Tab
                              label={updateCreativeStrings.detail}
                              id="tab-1"
                              aria-controls="tabpanel-1"
                            />
                          </Tabs>
                        </Box>
                        <TabPanel
                          className="creative-media-tab-panel"
                          value={selectedTab}
                          index={0}
                        >
                          {createMedia("", item, videoRef)}
                        </TabPanel>
                        <TabPanel
                          className="creative-media-tab-panel"
                          value={selectedTab}
                          index={1}
                        >
                          {createMedia("raw", item, videoRef)}
                        </TabPanel>
                      </>
                    ) : (
                      createMedia("", item, videoRef)
                    )}
                    <div className="creative-media-wrapper__metadata">
                      {metadataComponent({ creativeDetails: item })}
                    </div>
                  </div>
                </div>
              ))}
            </Carousel>
          </Paper>
        </Grid>
        {showProductListing &&
          creative.status !== MAPPING_KEYS_STATUSES.waitingProductRequested && (
            <Grid md={6} xs={12}>
              <Grid container direction="column" columns={12} height="100%">
                <Grid xs={12} flexGrow="1" sx={{ position: "relative" }}>
                  <FormControlLabel
                    className="update-creative__change-all"
                    control={<Checkbox checked={changeAll} />}
                    label={updateCreativeStrings.changeAll}
                    onChange={() => setChangeAll(!changeAll)}
                  />
                  {dataSource === DATA_SOURCE.pathmatics ? (
                    <PathmaticsProductListing
                      activationFilterOption={
                        ACTIVATION_FILTER_OPTIONS.creatives
                      }
                      mappingKey={creative}
                      allowSearch={true}
                      onSelectedProductRow={setSelectedProduct}
                      onSetValidVisual={handleSetValidVisual}
                      shouldRefresh={1}
                      isLoading={actionLoading || validVisualLoading}
                    />
                  ) : (
                    <ProductListing
                      activationFilterOption={
                        ACTIVATION_FILTER_OPTIONS.creatives
                      }
                      mappingKey={creative}
                      allowSearch={true}
                      onSelectedProductRow={setSelectedProduct}
                      onSetValidVisual={handleSetValidVisual}
                      shouldRefresh={1}
                      isLoading={actionLoading || validVisualLoading}
                    />
                  )}
                </Grid>
                <Grid
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={2}
                  paddingY={3}
                  justifyContent="space-between"
                >
                  <Grid
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={2}
                  >
                    <TextField
                      id="outlined-disabled"
                      label={updateCreativeStrings.paperClipLabel}
                      size="small"
                      value={paperClipLabel}
                      inputProps={{ maxLength: 255 }}
                      onChange={handlePaperClipFieldChange}
                    />
                    <Button
                      startIcon={<AttachFileOutlined />}
                      variant="outlined"
                      size="large"
                      disabled={disablePaperClipButton()}
                      onClick={handlePaperClipMappingKeys}
                    >
                      {updateCreativeStrings.paperClip}
                    </Button>
                  </Grid>
                  <Grid
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={2}
                  >
                    <Button
                      startIcon={<BlockOutlined />}
                      variant="outlined"
                      color="error"
                      size="large"
                      disabled={disableRejectButton()}
                      onClick={handleRejectMappingKeys}
                    >
                      {updateCreativeStrings.reject}
                    </Button>
                    <Button
                      startIcon={<SendOutlined />}
                      variant="outlined"
                      color="success"
                      size="large"
                      disabled={disableValidateButton()}
                      onClick={handleValidateMappingKeys}
                    >
                      {updateCreativeStrings.validate}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
      </Grid>
    </>
  );
};

UpdateCreative.propTypes = {
  dataSource: PropTypes.string.isRequired,
  creativeDetailsData: PropTypes.arrayOf(PropTypes.objectOf).isRequired,
  showProductListing: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
